import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

// Khalifa "who's active" view. Fetches the deny-all `events` analytics via the
// admin API and aggregates per person: logins, last-seen, pageviews, distinct
// pages, dwell — PLUS Akhara product signals: steps completed + Zor earned.
// This is the data the PRD's week-8 continue/kill decision depends on, so it is
// instrumented from Phase 1. Read-only; all server strings render as React text.

interface SerializedEvent {
  id: string;
  ts: string | null;
  type: string;
  uid: string;
  email: string;
  isStaff: boolean;
  path: string | null;
  dwellMs: number | null;
  trackId: string | null;
  stepId: string | null;
  reps: number | null;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'forbidden' }
  | { phase: 'ready'; events: SerializedEvent[] };

interface Summary {
  uid: string;
  email: string;
  logins: number;
  pageviews: number;
  pages: number;
  steps: number;
  reps: number;
  lastSeenTs: string | null;
}

function fmtDwellAbsent(): string {
  return '—';
}

function relativeTime(ts: string | null): string | null {
  if (!ts) return null;
  const diff = Date.now() - new Date(ts).getTime();
  if (!Number.isFinite(diff)) return null;
  const abs = Math.abs(diff);
  if (abs < 60_000) return 'just now';
  if (abs < 3_600_000) return `${Math.round(abs / 60_000)}m ago`;
  if (abs < 86_400_000) return `${Math.round(abs / 3_600_000)}h ago`;
  return `${Math.round(abs / 86_400_000)}d ago`;
}

function aggregate(events: SerializedEvent[]): Summary[] {
  const map = new Map<string, Summary>();
  const pathSets = new Map<string, Set<string>>();
  for (const ev of events) {
    if (!map.has(ev.email)) {
      map.set(ev.email, {
        uid: ev.uid,
        email: ev.email,
        logins: 0,
        pageviews: 0,
        pages: 0,
        steps: 0,
        reps: 0,
        lastSeenTs: null,
      });
    }
    const row = map.get(ev.email)!;
    if (ev.ts && (!row.lastSeenTs || ev.ts > row.lastSeenTs)) row.lastSeenTs = ev.ts;
    if (ev.type === 'login') row.logins += 1;
    else if (ev.type === 'pageview') {
      row.pageviews += 1;
      if (ev.path) {
        if (!pathSets.has(ev.email)) pathSets.set(ev.email, new Set());
        pathSets.get(ev.email)!.add(ev.path);
      }
    } else if (ev.type === 'step_complete') {
      row.steps += 1;
      row.reps += ev.reps ?? 0;
    }
  }
  for (const [email, paths] of pathSets) {
    const row = map.get(email);
    if (row) row.pages = paths.size;
  }
  return [...map.values()].sort((a, b) => (b.lastSeenTs ?? '').localeCompare(a.lastSeenTs ?? ''));
}

export default function AdminActivity() {
  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [tick, setTick] = useState(0);
  const navigate = useNavigate();
  const forbiddenRef = useRef(false);

  const reload = useCallback(() => {
    if (!forbiddenRef.current) setTick((t) => t + 1);
  }, []);

  useEffect(() => {
    if (forbiddenRef.current) return;
    let cancelled = false;
    setState({ phase: 'loading' });
    (async () => {
      const res = await api<{ events?: SerializedEvent[] }>('GET', '/api/admin/activity?limit=2000');
      if (cancelled || forbiddenRef.current) return;
      if (res.status === 403) {
        forbiddenRef.current = true;
        setState({ phase: 'forbidden' });
        return;
      }
      if (!res.ok) {
        setState({
          phase: 'error',
          message: res.status === 0 ? 'Network error — check your connection.' : `Failed (HTTP ${res.status}).`,
        });
        return;
      }
      setState({ phase: 'ready', events: Array.isArray(res.data?.events) ? res.data.events! : [] });
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const rows = state.phase === 'ready' ? aggregate(state.events) : [];

  return (
    <div>
      <p className="text-sm text-ink-500 dark:text-paper-300">
        Khalifa view — are people actually training? Logins and paintras are server-emitted (can’t be faked).
      </p>

        {state.phase === 'forbidden' && (
          <div className="mt-6 rounded-xl border border-warn-500/40 bg-paper-0 p-4 text-sm dark:bg-clay-surface" role="alert">
            <p className="font-semibold text-ink-900 dark:text-paper-50">Not authorized</p>
            <p className="mt-1 text-ink-500 dark:text-paper-300">This view is for Khalifas (admins) only.</p>
          </div>
        )}

        {state.phase === 'loading' && <p className="mt-6 text-sm text-ink-400">Loading…</p>}

        {state.phase === 'error' && (
          <div className="mt-6 text-sm text-terracotta-700">
            {state.message}{' '}
            <button onClick={reload} className="underline">
              Retry
            </button>
          </div>
        )}

        {state.phase === 'ready' && (
          <>
            <div className="mt-5 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-ink-700 dark:text-paper-200">
                {rows.length} {rows.length === 1 ? 'person' : 'people'}
              </h2>
              <button
                onClick={reload}
                className="rounded-lg border border-rule px-2.5 py-1 text-xs font-medium text-ink-500 hover:bg-paper-100 dark:border-clay-rule dark:hover:bg-clay-raised"
              >
                Refresh
              </button>
            </div>
            <div className="mt-2 overflow-x-auto rounded-xl border border-rule dark:border-clay-rule">
              <table className="w-full text-sm">
                <thead className="bg-paper-100 text-left text-xs uppercase tracking-wide text-ink-400 dark:bg-clay-raised/40">
                  <tr>
                    <th className="px-3 py-2 font-medium">Person</th>
                    <th className="px-3 py-2 text-right font-medium">Logins</th>
                    <th className="px-3 py-2 text-right font-medium">Paintras</th>
                    <th className="px-3 py-2 text-right font-medium">Zor</th>
                    <th className="px-3 py-2 text-right font-medium">Pages</th>
                    <th className="px-3 py-2 font-medium">Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-3 py-6 text-center text-ink-400">
                        No activity recorded yet.
                      </td>
                    </tr>
                  ) : (
                    rows.map((r) => (
                      <tr
                        key={r.email}
                        onClick={() => navigate(`/admin/people/${encodeURIComponent(r.uid)}`)}
                        title="View this person’s activity"
                        className="cursor-pointer border-t border-rule hover:bg-paper-100 dark:border-clay-rule dark:hover:bg-clay-raised/40"
                      >
                        <td className="px-3 py-2 font-mono text-xs text-ink-700 dark:text-paper-200">{r.email}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.logins}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium text-terracotta-600 dark:text-terracotta-300">
                          {r.steps}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.reps}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.pages || fmtDwellAbsent()}</td>
                        <td className="px-3 py-2 text-ink-500 dark:text-paper-300">
                          {relativeTime(r.lastSeenTs) ?? '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
    </div>
  );
}
